import React, { createContext, useContext, useEffect, useState } from 'react';
import { omit } from 'lodash';

import { useAuth } from 'contexts/auth';
import courseService from 'services/course';

const UserContext = createContext();
const useUser2 = () => useContext(UserContext);

const mergeAccountsAndUsers = (accounts, users) => {
    return accounts
        .filter(({ active }) => active)
        .map((account) => {
            const user = users.find(({ id }) => id === account.user_id);
            const userAccounts = accounts.filter(({ user_id }) => user_id === user.id);
            const isPending = userAccounts.some(({ login_activity }) => Boolean(login_activity));
            return {
                ...omit(account, ['_id', 'id']),
                ...omit(user, ['_id', 'id']),
                owner_account_id: account.id,
                user_id: user.id,
                is_pending: isPending,
            };
        });
};

const UserProvider = (props) => {
    const { auth } = useAuth();

    const [course, setCourse] = useState(null);
    const [groups, setGroups] = useState(null);
    const [currentGroupId, setCurrentGroupId] = useState(null);
    const [accountUsers, setAccountUsers] = useState(null); // account document and user document merged
    const [distribution, setDistribution] = useState(null);
    const [formatives, setFormatives] = useState(null);

    const isLoading = !course || !groups || !accountUsers;
    const currentGroup = groups?.find(({ id }) => id === currentGroupId);
    const instructorAccountUsers = accountUsers?.filter(({ role }) => role === 'instructor');
    const studentAccountUsers = accountUsers?.filter(({ role }) => role === 'student');
    const currentGroupAccountUsers =
        currentGroup && accountUsers ? accountUsers.filter(({ group_id }) => group_id === currentGroup.id) : null;

    const updateCurrentGroup = (changes) => {
        setGroups((current) =>
            current.map((group) => {
                return group.id === currentGroupId
                    ? {
                          ...group,
                          ...changes,
                      }
                    : group;
            })
        );
    };

    const addAccountUser = (accountUser) => {
        setAccountUsers((current) => [...current, accountUser]);
    };

    const removeAccountUser = (accountUser) => {
        setAccountUsers((current) =>
            current.filter(({ owner_account_id }) => owner_account_id !== accountUser.owner_account_id)
        );
    };

    const updateGroupsAndAccounts = (groups, accounts) => {
        setGroups(groups);
        setAccountUsers((current) =>
            current.map((accountUser) => {
                const newAccount = accounts.find(({ user_id }) => user_id === accountUser.user_id);
                if (!newAccount) return accountUser;
                return {
                    ...omit(accountUser, ['group_id']),
                    ...omit(newAccount, ['_id', 'id']),
                    owner_account_id: newAccount.id,
                };
            })
        );
    };

    useEffect(() => {
        (async () => {
            const course = await courseService.findCourse(auth.course_id);
            setCourse(course);
        })();
    }, [auth.course_id]);

    useEffect(() => {
        if (!course) return;
        (async () => {
            const [groups, accounts, users, distribution, formatives] = await Promise.all([
                await courseService.listCourseGroups(course.id),
                await courseService.listCourseAccounts(course.id),
                await courseService.listCourseUsers(course.id),
                await courseService.findCourseDistribution(course.id),
                await courseService.listCourseFormatives(course.id),
            ]);
            setGroups(groups);
            setAccountUsers(mergeAccountsAndUsers(accounts, users));
            setDistribution(distribution);
            setFormatives(formatives);
        })();
    }, [course]);

    useEffect(() => {
        if (auth.role === 'student' && auth.group_id) {
            setCurrentGroupId(auth.group_id);
        }
    }, [auth.role, auth.group_id]);

    return (
        <UserContext.Provider
            {...props}
            value={{
                isLoading,

                course,
                groups,
                currentGroup,
                setCurrentGroupId,
                updateCurrentGroup,

                accountUsers,
                instructorAccountUsers, // accountUsers with role 'instructor'
                studentAccountUsers, // accountUsers with role 'student'
                currentGroupAccountUsers, // accountUsers with role 'student' in the current group
                addAccountUser,
                removeAccountUser,

                updateGroupsAndAccounts,

                distribution,
                setDistribution,

                formatives,
                setFormatives,
            }}
        />
    );
};

export { useUser2 };
export default UserProvider;
